import React, { useEffect, useState } from "react";
import { getMySentEmails } from "../services/Mail_service";
import { toast } from "react-toastify";
import { paginate, ITEMS_PER_PAGE } from "../utils/functions/pagination";
import { PaginationControls } from "./PaginationControls";
import { Typography, Input, Checkbox, Card, Space, Tooltip, Badge } from "antd";
import { getUserDetails } from "../services/User_service";
import { useNavigate } from "react-router-dom";
import { Offer, getAllOffers } from "../services/offreService";
import OfferCards from "./OfferCards";
import dayjs from "dayjs";
import isBetween from 'dayjs/plugin/isBetween'; // IMPORTANT: Ajouter cet import
import { DatePicker } from "antd";
import { 
  SearchOutlined, 
  MailOutlined, 
  UserOutlined, 
  CalendarOutlined,
  ImportOutlined,
  EyeOutlined,
  DownOutlined,
  UpOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { FaSpinner } from "react-icons/fa";


// IMPORTANT: Activer le plugin
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Types
interface EmailDetails {
  name?: string;
  profession?: string;
  dirigeant?: string;
  [key: string]: any;
}

interface Email {
  id: number;
  recipient: string;
  subject: string;
  content: string;
  details: string | EmailDetails;
  createdAt: string;
  [key: string]: any;
}

function MesMailsEnvoyes() {
  // States
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalContent, setModalContent] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEmails, setFilteredEmails] = useState<Email[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [activeOffers, setActiveOffers] = useState<Offer[]>([]);
  const [showOffers, setShowOffers] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const [totalPages, setTotalPages] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const [loadingPage, setLoadingPage] = useState(false);

  const navigate = useNavigate();
  const itemsPerPage = 10;

  // Styles avec typage correct
  const styles = {
    container: {
      padding: "24px",
      maxWidth: "1400px",
      margin: "0 auto"
    } as const,
    header: {
      marginBottom: "24px"
    } as const,
    filterCard: {
      marginBottom: "24px",
      padding: "20px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
    } as const,
    filterRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "16px",
      alignItems: "end"
    } as const,
    tableContainer: {
      overflowX: "auto" as const,
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      background: "#fff"
    } as const,
    table: {
      width: "100%",
      borderCollapse: "collapse" as const,
      minWidth: "800px"
    } as const,
    th: {
      backgroundColor: "#4BB0C0",
      color: "white",
      padding: "16px 12px",
      fontSize: "14px",
      fontWeight: "600",
      textAlign: "left" as const,
      borderBottom: "2px solid #3a9aa8"
    } as const,
    td: {
      padding: "14px 12px",
      borderBottom: "1px solid #e8e8e8",
      fontSize: "14px"
    } as const,
    expandedContent: {
      background: "#f8f9fa",
      padding: "24px",
      borderRadius: "12px",
      margin: "16px",
      border: "1px solid #e5e7eb"
    } as const,
    badge: {
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "500"
    } as const
  };

  // Effets
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await getUserDetails();
        setUser(userData);
      } catch (error) {
        toast.error("Impossible de récupérer les infos utilisateur");
      }
    };
    fetchUser();
  }, []);

const fetchData = async (page: number = 1) => {
  setLoading(true);
  setLoadingPage(true);
  try {
    const response = await getMySentEmails(page, itemsPerPage);
    setEmails(response.data);
    setFilteredEmails(response.data);
    setTotalPages(response.pagination.totalPages);
    setTotalEmails(response.pagination.total);
    setCurrentPage(response.pagination.page);
  } catch (error) {
    toast.error("Impossible de récupérer vos emails envoyés");
  } finally {
    setLoading(false);
    setLoadingPage(false);
  }
};

useEffect(() => {
  fetchData(1);
}, []);


const handlePageChange = (newPage: number) => {
  setCurrentPage(newPage);
  fetchData(newPage);
};


  useEffect(() => {
    handleSearch();
  }, [searchTerm, startDate, endDate]);

  // Fonctions utilitaires
  const parseDetails = (details: any): EmailDetails => {
    if (!details) return {};
    if (typeof details === 'string') {
      try {
        return JSON.parse(details);
      } catch (e) {
        console.error('Error parsing details:', e);
        return {};
      }
    }
    return details;
  };

  const handleSearch = () => {
    let filtered = emails;

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(email => {
        const recipient = email.recipient?.toLowerCase() || "";
        const details = parseDetails(email.details);
        const name = details.name?.toLowerCase() || "";
        const profession = details.profession?.toLowerCase() || "";
        
        return recipient.includes(lowerSearch) ||
              name.includes(lowerSearch) ||
              profession.includes(lowerSearch) ||
              email.subject?.toLowerCase().includes(lowerSearch);
      });
    }

    if (startDate && endDate) {
      const start = dayjs(startDate).startOf('day');
      const end = dayjs(endDate).endOf('day');
      filtered = filtered.filter((email) => {
        const emailDate = dayjs(email.createdAt);
        return emailDate.isBetween(start, end, null, '[]');
      });
    }

    setFilteredEmails(filtered);
    setCurrentPage(1);
  };

  const handleSelectRecipient = (recipient: string, checked: boolean) => {
    setSelectedRecipients(prev =>
      checked ? [...prev, recipient] : prev.filter(r => r !== recipient)
    );
  };

 const handleSelectAll = (checked: boolean) => {
  if (checked) {
    const allRecipients = filteredEmails.map(email => email.recipient);
    setSelectedRecipients(Array.from(new Set([...selectedRecipients, ...allRecipients])));
  } else {
    const pageRecipients = filteredEmails.map(email => email.recipient);
    setSelectedRecipients(selectedRecipients.filter(r => !pageRecipients.includes(r)));
  }
};

  const handleImportToSendEmail = () => {
    if (selectedRecipients.length === 0) {
      toast.warning("Veuillez sélectionner au moins un destinataire");
      return;
    }
    localStorage.setItem("importedRecipients", JSON.stringify(selectedRecipients));
    toast.success(`${selectedRecipients.length} destinataire(s) sélectionné(s)`);
    navigate("/home/gestion-emails?tab=EnvoyerMail");
  };


  const toggleExpand = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const closeModal = () => setModalContent(null);

  // Pagination
  
  
const allPageRecipientsSelected = filteredEmails.length > 0 &&
  filteredEmails.every(email => selectedRecipients.includes(email.recipient));

  
  // Version mobile d'une ligne
  const MobileEmailCard = ({ email, index }: { email: Email; index: number }) => {
    const details = parseDetails(email.details);
    const isExpanded = expandedRow === email.id;
    const isSelected = selectedRecipients.includes(email.recipient);

    return (
      <Card 
        style={{ 
          marginBottom: "12px",
          borderRadius: "8px",
          border: isSelected ? "2px solid #4BB0C0" : "1px solid #f0f0f0"
        }}
        bodyStyle={{ padding: "12px" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
          <Checkbox
            checked={isSelected}
            onChange={(e) => handleSelectRecipient(email.recipient, e.target.checked)}
          />
          <Badge status="success" text={new Date(email.createdAt).toLocaleDateString()} />
        </div>

        <div style={{ display: "grid", gap: "8px" }}>
          <div><MailOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> {email.recipient}</div>
          {details.name && <div><UserOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> {details.name}</div>}
          {details.profession && <div><FileTextOutlined style={{ color: "#4BB0C0", marginRight: "8px" }} /> {details.profession}</div>}
        </div>

        <div style={{ marginTop: "12px", textAlign: "right" }}>
          <button
            onClick={() => toggleExpand(email.id)}
            style={{
              background: "none",
              border: "none",
              color: "#4BB0C0",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            {isExpanded ? "Réduire ▲" : "Voir détails ▼"}
          </button>
        </div>

        {isExpanded && (
          <div style={{ marginTop: "16px", padding: "12px", background: "#f9f9f9", borderRadius: "8px" }}>
            <h4 style={{ margin: "0 0 12px 0", fontSize: "16px", color: "#333" }}>Détails complets</h4>
            <div style={{ display: "grid", gap: "8px" }}>
              <div><strong>Objet:</strong> {email.subject}</div>
              <div>
                <strong>Message:</strong>
                <div
                  style={{
                    marginTop: "8px",
                    padding: "12px",
                    background: "#fff",
                    borderRadius: "4px",
                    maxHeight: "150px",
                    overflow: "auto"
                  }}
                  dangerouslySetInnerHTML={{ __html: email.content || "" }}
                />
              </div>
            </div>
          </div>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header avec titre */}
      <div style={styles.header}>
        <Title level={2} style={{ color: "#333", marginBottom: "8px" }}>
          Mes Emails Envoyés
        </Title>
        <Text type="secondary">
          Gérez et consultez l'historique de vos communications
        </Text>
      </div>

      {/* Filtres et recherche */}
      <Card style={styles.filterCard}>
        <div style={styles.filterRow}>
          <div>
            <Text strong>Recherche</Text>
            <Input
              placeholder="Email, nom, profession..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined style={{ color: "#4BB0C0" }} />}
              allowClear
              size="large"
            />
          </div>

          <div>
            <Text strong>Période</Text>
            <RangePicker
              value={
                startDate && endDate
                  ? [dayjs(startDate), dayjs(endDate)]
                  : null
              }
              onChange={(dates) => {
                if (dates) {
                  setStartDate(dates[0]?.format('YYYY-MM-DD') || null);
                  setEndDate(dates[1]?.format('YYYY-MM-DD') || null);
                } else {
                  setStartDate(null);
                  setEndDate(null);
                }
              }}
              format="DD/MM/YYYY"
              placeholder={['Début', 'Fin']}
              style={{ width: '100%' }}
              size="large"
            />
          </div>

          {selectedRecipients.length > 0 && (
            <div>
              <Tooltip title="Envoyer un email aux destinataires sélectionnés">
                <button
                  onClick={handleImportToSendEmail}
                  className="btn"
                  style={{
                    backgroundColor: "#4BB0C0",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    width: "100%"
                  }}
                >
                  <ImportOutlined /> Envoyer un email ({selectedRecipients.length})
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </Card>

      {/* Contenu principal */}
      {showOffers ? (
        <div>
          <Title level={4} style={{ marginBottom: "20px" }}>Offres disponibles</Title>
          {activeOffers.length > 0 ? (
            <OfferCards
              offers={activeOffers}
              setShowModalOffre={() => {}}
              setSelectedOffer={() => {}}
              handleDelete={async () => {}}
              userRole={user?.role === "ADMIN" ? "ADMIN" : "CLIENT"}
            />
          ) : (
            <Card>
              <Text>Aucune offre disponible pour le moment.</Text>
            </Card>
          )}
        </div>
      ) : (
        <>
          {filteredEmails.length > 0 ? (
            <>
              {/* Version Desktop - Tableau */}
              <div className="d-none d-md-block" style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, width: "50px" }}>
                        <Checkbox
                          checked={allPageRecipientsSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th style={styles.th}>Date d'envoi</th>
                      <th style={styles.th}>Destinataire</th>
                      <th style={styles.th}>Nom & Prénom</th>
                      <th style={styles.th}>Profession</th>
                      <th style={styles.th}>Dirigeant</th>
                      <th style={styles.th}>Contenu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmails.map((email) => {
                      const details = parseDetails(email.details);
                      const isExpanded = expandedRow === email.id;

                      return (
                        <React.Fragment key={email.id}>
                          <tr style={{ cursor: "pointer" }} onClick={() => toggleExpand(email.id)}>
                            <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedRecipients.includes(email.recipient)}
                                onChange={(e) => handleSelectRecipient(email.recipient, e.target.checked)}
                              />
                            </td>
                            <td style={styles.td}>
                              <Space>
                                <CalendarOutlined style={{ color: "#4BB0C0" }} />
                                {new Date(email.createdAt).toLocaleDateString()}
                              </Space>
                            </td>
                            <td style={styles.td}>
                              <Tooltip title="Email du destinataire">
                                <Space>
                                  <MailOutlined style={{ color: "#4BB0C0" }} />
                                  {email.recipient}
                                </Space>
                              </Tooltip>
                            </td>
                            <td style={styles.td}>{details.name || "-"}</td>
                            <td style={styles.td}>{details.profession || "-"}</td>
                            <td style={styles.td}>{details.dirigeant || "-"}</td>
                            <td style={styles.td}>
                              <Space>
                                <EyeOutlined style={{ color: "#4BB0C0" }} />
                                {isExpanded ? <UpOutlined /> : <DownOutlined />}
                              </Space>
                            </td>
                          </tr>

                          {/* Ligne expandée pour les détails */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={7} style={{ padding: 0, background: "#f9f9f9" }}>
                                <div style={styles.expandedContent}>
                                  <h4 style={{ margin: "0 0 16px 0", color: "#333" }}>
                                    Détails de l'email
                                  </h4>
                                  <div style={{ display: "grid", gap: "16px" }}>
                                    <div>
                                      <Text type="secondary">Objet</Text>
                                      <div style={{ fontWeight: 500 }}>{email.subject}</div>
                                    </div>
                                    <div>
                                      <Text type="secondary">Message</Text>
                                      <div
                                        style={{
                                          padding: "16px",
                                          background: "#fff",
                                          borderRadius: "8px",
                                          maxHeight: "300px",
                                          overflow: "auto"
                                        }}
                                        dangerouslySetInnerHTML={{ __html: email.content || "" }}
                                      />
                                    </div>
                                    {details.commentaires && (
                                      <div>
                                        <Text type="secondary">Commentaires</Text>
                                        <div>{details.commentaires}</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Version Mobile - Cards */}
              <div className="d-block d-md-none">
                {filteredEmails.map((email, index) => (
                  <MobileEmailCard key={email.id} email={email} index={index} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                  {loadingPage && <FaSpinner className="animate-spin" style={{ marginLeft: '10px' }} />}
                </div>
              )}
            </>
          ) : (
            <Card style={{ textAlign: "center", padding: "40px" }}>
              <MailOutlined style={{ fontSize: "48px", color: "#ccc", marginBottom: "16px" }} />
              <Title level={4}>Aucun email trouvé</Title>
              <Text type="secondary">
                Vous n'avez pas encore envoyé d'emails ou aucun résultat ne correspond à votre recherche.
              </Text>
            </Card>
          )}
        </>
      )}

      {/* Modal pour le contenu (gardé pour compatibilité) */}
      {modalContent && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "white",
              padding: 24,
              maxWidth: "90%",
              maxHeight: "80%",
              overflow: "auto",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: modalContent }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default MesMailsEnvoyes;